import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Tag, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { getStoredUtms } from "@/lib/utils";

const FEATURES = [
  "Access to all case workspaces",
  "Community discussion boards",
  "AI Assistant inside every case",
  "Detective Notes from Christine Burke",
  "Full document & video library",
  "Case update notifications",
];

const PLANS = {
  monthly: { price: 4.99, unit: "/ month", note: "Billed monthly · cancel anytime" },
  annual: { price: 49.99, unit: "/ year", note: "Billed annually · ~2 months free" },
};

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null); // {code,label,final_price,free}

  const price = PLANS[plan].price;
  const finalPrice = applied ? applied.final_price : price;

  const revalidate = async (newPlan, theCode) => {
    if (!theCode) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: theCode, plan: newPlan });
      if (data.valid) setApplied(data);
      else setApplied(null);
    } catch { setApplied(null); }
  };

  const switchPlan = (p) => {
    setPlan(p);
    if (applied) revalidate(p, applied.code);
  };

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: code.trim(), plan });
      if (data.valid) {
        setApplied(data);
        toast.success(`${data.label} applied!`);
      } else {
        setApplied(null);
        toast.error(data.message || "Invalid coupon");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not validate coupon");
    } finally { setApplying(false); }
  };

  const removeCoupon = () => { setApplied(null); setCode(""); };

  const subscribe = async () => {
    if (!user) { navigate("/register"); return; }
    if (user.role === "admin" || user.subscription_status === "active") { navigate("/cases"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", {
        origin_url: window.location.origin,
        coupon: applied ? applied.code : "",
        plan,
        ...getStoredUtms(),
      });
      if (data.free) {
        await refreshUser();
        toast.success("Membership activated — welcome, Founding Member!");
        navigate("/cases");
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
        <div className="label-mono mb-4">Membership</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">One membership. Full access.</h1>
        <p className="mt-5 text-white/60">Help Solve The Case™ is built for serious case-followers. Auto-renews so you never lose access — cancel anytime in two clicks.</p>

        {/* Billing toggle */}
        <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-white/10 bg-surface p-1" data-testid="billing-toggle">
          <button
            data-testid="plan-monthly-btn"
            onClick={() => switchPlan("monthly")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${plan === "monthly" ? "bg-gold text-charcoal" : "text-white/60 hover:text-white"}`}
          >
            Monthly
          </button>
          <button
            data-testid="plan-annual-btn"
            onClick={() => switchPlan("annual")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${plan === "annual" ? "bg-gold text-charcoal" : "text-white/60 hover:text-white"}`}
          >
            Annual
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${plan === "annual" ? "bg-charcoal/20 text-charcoal" : "bg-green-400/15 text-green-400"}`}>SAVE $10</span>
          </button>
        </div>

        <div className="mt-8 card-surface rounded-md p-8 sm:p-10 text-left grain max-w-md mx-auto">
          <div className="flex items-baseline gap-2">
            {applied && finalPrice !== price && (
              <span className="font-display text-2xl font-bold text-white/30 line-through">${price.toFixed(2)}</span>
            )}
            <span className="font-display text-5xl font-bold text-white" data-testid="plan-price">${finalPrice.toFixed(2)}</span>
            <span className="text-white/50">{PLANS[plan].unit}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-block label-mono text-gold border border-gold/30 rounded-sm px-2 py-1 bg-gold/5">Founding Member</span>
            {plan === "annual" && <span className="text-xs text-white/40 font-mono">≈ ${(finalPrice / 12).toFixed(2)}/mo</span>}
            {applied && <span className="inline-block label-mono text-green-400 border border-green-400/30 rounded-sm px-2 py-1 bg-green-400/5">{applied.label}</span>}
          </div>
          <p className="mt-2 text-xs text-white/40 font-mono">{PLANS[plan].note}</p>

          <ul className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          {/* Coupon */}
          <div className="mt-8">
            {applied ? (
              <div className="flex items-center justify-between rounded-sm border border-green-400/30 bg-green-400/5 px-3 py-2.5">
                <span className="flex items-center gap-2 text-sm text-green-400 font-mono"><Tag className="w-4 h-4" /> {applied.code} · {applied.label}{applied.free && " · FREE"}</span>
                <button onClick={removeCoupon} data-testid="remove-coupon-btn" className="text-white/40 hover:text-red-400 transition-colors"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                    placeholder="Coupon code" data-testid="coupon-input"
                    className="w-full bg-charcoal border border-white/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-white uppercase tracking-wider focus:outline-none focus:border-gold/50" />
                </div>
                <button onClick={applyCoupon} disabled={applying || !code.trim()} data-testid="apply-coupon-btn"
                  className="px-4 py-2.5 text-sm font-semibold border border-white/15 text-white rounded-sm hover:border-gold/40 transition-colors disabled:opacity-50">
                  {applying ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors disabled:opacity-60">
            {loading ? "Processing…" : user?.subscription_status === "active" ? "Go to Cases"
              : applied?.free ? "Activate free membership"
              : applied?.trial ? `Start ${applied.free_trial_days}-day free trial`
              : `Subscribe — $${finalPrice.toFixed(2)}${plan === "annual" ? "/yr" : "/mo"}`}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
          <p className="mt-4 text-[11px] text-center text-white/30 font-mono">{applied?.free ? "No payment required with this code"
            : applied?.trial ? `Free for ${applied.free_trial_days} days, then $${finalPrice.toFixed(2)}${plan === "annual" ? "/yr" : "/mo"} · cancel anytime`
            : "Secured by Stripe · auto-renews · cancel anytime"}</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
